import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import Layout from '../../components/Layout';
import DialogModal from '../../components/DialogModal';
import hostelService from '../../services/hostelService';
import studentsService from '../../services/studentsService';
import classesService from '../../services/classesService';
import { FormSelect } from '../../components/finance';
import { MagnifyingGlass, Plus, PencilSimple, CheckCircle, Clock, Download, Eye, X, CircleNotch, ArrowLeft, ChartBar, CreditCard } from '@phosphor-icons/react';
import './hostel.css';

function HostelFeesPage() {
  const navigate = useNavigate();
  // ========== State Management ==========
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('fees'); // 'fees', 'structures', 'bulk'
  
  // Fee Management
  const [fees, setFees] = useState([]);
  const [filteredFees, setFilteredFees] = useState([]);
  const [feeSearchTerm, setFeeSearchTerm] = useState('');
  const [feeStatusFilter, setFeeStatusFilter] = useState('all');
  
  // Fee Structure Management
  const [feeStructures, setFeeStructures] = useState([]);
  const [filteredStructures, setFilteredStructures] = useState([]);
  const [structureSearchTerm, setStructureSearchTerm] = useState('');
  const [selectedHostel, setSelectedHostel] = useState('all');
  
  // Hostels and Students
  const [hostels, setHostels] = useState([]);
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  
  // Forms
  const [showFeeStructureForm, setShowFeeStructureForm] = useState(false);
  const [showFeeForm, setShowFeeForm] = useState(false);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [showBulkBillingForm, setShowBulkBillingForm] = useState(false);
  const [showFeeDetail, setShowFeeDetail] = useState(false);
  const [showStructureDetail, setShowStructureDetail] = useState(false);
  
  const [selectedFee, setSelectedFee] = useState(null);
  const [selectedStructure, setSelectedStructure] = useState(null);
  
  const [structureFormData, setStructureFormData] = useState({
    hostel_id: '',
    academic_term_id: '',
    fee_type: 'monthly',
    amount: '',
    description: '',
    due_date: '',
    gl_revenue_account_code: '4150',
    is_active: true
  });
  
  const [feeFormData, setFeeFormData] = useState({
    student_id: '',
    hostel_id: '',
    academic_term_id: '',
    fee_type: 'monthly',
    amount_due: '',
    discount: 0,
    due_date: '',
    notes: ''
  });
  
  const [paymentFormData, setPaymentFormData] = useState({
    amount_paid: '',
    payment_method: 'cash',
    receipt_number: '',
    payment_date: new Date().toISOString().split('T')[0]
  });
  
  const [bulkBillingData, setBulkBillingData] = useState({
    hostel_id: '',
    academic_term_id: '',
    fee_structure_id: '',
    class_id: '',
    bulk_amount: '',
    due_date: ''
  });
  const [dialogConfig, setDialogConfig] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'warning',
    primaryAction: null,
    secondaryAction: null
  });
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const [pendingDeleteType, setPendingDeleteType] = useState(null);
  
  // ========== Data Loading ==========
  useEffect(() => {
    loadData();
  }, []);
  
  useEffect(() => {
    filterFees();
  }, [fees, feeSearchTerm, feeStatusFilter, filterFees]);
  
  useEffect(() => {
    filterStructures();
  }, [feeStructures, structureSearchTerm, selectedHostel, filterStructures]);
  
  const loadData = async () => {
    try {
      setLoading(true);
      const [feesData, structuresData, hostelsData, studentsData, classesData] = await Promise.all([
        hostelService.listFees().catch(() => []),
        hostelService.listFeeStructures().catch(() => []),
        hostelService.listHostels().catch(() => []),
        studentsService.listStudents().catch(() => []),
        classesService.listClasses().catch(() => [])
      ]);
      
      const studentsList = Array.isArray(studentsData) ? studentsData : studentsData.students || studentsData.items || [];
      
      setFees(Array.isArray(feesData) ? feesData : []);
      setFeeStructures(Array.isArray(structuresData) ? structuresData : []);
      setHostels(Array.isArray(hostelsData) ? hostelsData : []);
      setStudents(studentsList);
      setClasses(Array.isArray(classesData) ? classesData : []);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load hostel fee data');
    } finally {
      setLoading(false);
    }
  };
  
  // ========== Filter Functions ==========
  const filterFees = React.useCallback(() => {
    let filtered = fees;
    
    if (feeStatusFilter !== 'all') {
      filtered = filtered.filter(f => f.is_paid === (feeStatusFilter === 'paid'));
    }
    
    if (feeSearchTerm) {
      filtered = filtered.filter(f =>
        getStudentName(f.student_id).toLowerCase().includes(feeSearchTerm.toLowerCase()) ||
        getHostelName(f.hostel_id).toLowerCase().includes(feeSearchTerm.toLowerCase())
      );
    }
    
    setFilteredFees(filtered);
  }, [fees, feeSearchTerm, feeStatusFilter, students, hostels]);
  
  const filterStructures = React.useCallback(() => {
    let filtered = feeStructures;
    
    if (selectedHostel !== 'all') {
      filtered = filtered.filter(s => s.hostel_id === selectedHostel);
    }
    
    if (structureSearchTerm) {
      filtered = filtered.filter(s =>
        getHostelName(s.hostel_id).toLowerCase().includes(structureSearchTerm.toLowerCase())
      );
    }
    
    setFilteredStructures(filtered);
  }, [feeStructures, structureSearchTerm, selectedHostel, hostels]);
  
  // ========== Helper Functions ==========
  const getStudentName = (studentId) => {
    const student = students.find(s => s.student_id === studentId);
    return student ? `${student.student_id} - ${student.first_name} ${student.last_name}` : 'Unknown';
  };
  
  const getHostelName = (hostelId) => {
    const hostel = hostels.find(h => h.id === hostelId);
    return hostel ? hostel.hostel_name : 'Unknown';
  };
  
  const getClassName = (classId) => {
    const cls = classes.find(c => c.id === classId);
    return cls ? cls.class_name : 'Unknown';
  };
  
  // ========== Fee Structure Operations ==========
  const handleCreateStructure = async () => {
    if (!structureFormData.hostel_id || !structureFormData.amount) {
      toast.error('Please fill in required fields');
      return;
    }
    
    try {
      if (selectedStructure) {
        // Update existing structure
        await hostelService.updateFeeStructure(selectedStructure.id, structureFormData);
        toast.success('Fee structure updated successfully');
      } else {
        // Create new structure
        await hostelService.createFeeStructure(structureFormData);
        toast.success('Fee structure created successfully');
      }
      setShowFeeStructureForm(false);
      setSelectedStructure(null);
      setStructureFormData({
        hostel_id: '',
        academic_term_id: '',
        fee_type: 'monthly',
        amount: '',
        description: '',
        due_date: '',
        gl_revenue_account_code: '4150',
        is_active: true
      });
      loadData();
    } catch (error) {
      toast.error(selectedStructure ? 'Failed to update fee structure' : 'Failed to create fee structure');
    }
  };
  
  const handleEditStructure = (structure) => {
    setSelectedStructure(structure);
    setStructureFormData({
      hostel_id: structure.hostel_id,
      academic_term_id: structure.academic_term_id || '',
      fee_type: structure.fee_type,
      amount: structure.amount,
      description: structure.description || '',
      due_date: structure.due_date || '',
      gl_revenue_account_code: structure.gl_revenue_account_code || '4150',
      is_active: structure.is_active
    });
    setShowFeeStructureForm(true);
  };
  
  const handleViewStructureDetails = (structure) => {
    setSelectedStructure(structure);
    setShowStructureDetail(true);
  };
  
  const showDialog = (config) => {
    setDialogConfig({
      isOpen: true,
      ...config
    });
  };

  const closeDialog = () => {
    setDialogConfig(prev => ({
      ...prev,
      isOpen: false
    }));
    setPendingDeleteId(null);
    setPendingDeleteType(null);
  };
  
  const handleDeleteStructure = (id) => {
    setPendingDeleteId(id);
    setPendingDeleteType('structure');
    showDialog({
      title: 'Delete Fee Structure',
      message: 'Are you sure you want to delete this fee structure?',
      type: 'warning',
      primaryAction: {
        label: 'Delete',
        onClick: async () => {
          closeDialog();
          try {
            await hostelService.deleteFeeStructure(id);
            toast.success('Fee structure deleted');
            loadData();
          } catch (error) {
            toast.error('Failed to delete fee structure');
          }
        }
      },
      secondaryAction: {
        label: 'Cancel',
        onClick: closeDialog
      }
    });
  };
  
  // ========== Fee Operations ==========
  const handleCreateFee = async () => {
    if (!feeFormData.student_id || !feeFormData.hostel_id || !feeFormData.amount_due) {
      toast.error('Please fill in required fields');
      return;
    }
    
    try {
      if (selectedFee) {
        // Update existing fee
        await hostelService.updateFee(selectedFee.id, feeFormData);
        toast.success('Fee updated successfully');
      } else {
        // Create new fee
        await hostelService.createFee(feeFormData);
        toast.success('Fee created successfully');
      }
      setShowFeeForm(false);
      setSelectedFee(null);
      setFeeFormData({
        student_id: '',
        hostel_id: '',
        academic_term_id: '',
        fee_type: 'monthly',
        amount_due: '',
        discount: 0,
        due_date: '',
        notes: ''
      });
      loadData();
    } catch (error) {
      toast.error(selectedFee ? 'Failed to update fee' : 'Failed to create fee');
    }
  };
  
  const handleEditFee = (fee) => {
    setSelectedFee(fee);
    setFeeFormData({
      student_id: fee.student_id,
      hostel_id: fee.hostel_id,
      academic_term_id: fee.academic_term_id || '',
      fee_type: fee.fee_type,
      amount_due: fee.amount_due,
      discount: fee.discount || 0,
      due_date: fee.due_date || '',
      notes: fee.notes || ''
    });
    setShowFeeForm(true);
  };
  
  const handleViewFeeDetails = (fee) => {
    setSelectedFee(fee);
    setShowFeeDetail(true);
  };
  
  const handleRecordPayment = async () => {
    if (!paymentFormData.amount_paid) {
      toast.error('Enter payment amount');
      return;
    }
    
    try {
      // Calculate new total amount paid (cumulative)
      const paymentAmount = parseFloat(paymentFormData.amount_paid);
      const currentTotal = selectedFee.amount_paid || 0;
      const newTotalAmountPaid = currentTotal + paymentAmount;
      
      // Calculate outstanding balance after payment
      const outstandingAfterPayment = selectedFee.amount_due - newTotalAmountPaid - (selectedFee.discount || 0);
      
      // Prevent overpayment
      if (outstandingAfterPayment < 0) {
        toast.error(`Payment exceeds due amount. Outstanding: GHS ${Math.max(0, selectedFee.amount_due - currentTotal - (selectedFee.discount || 0)).toFixed(2)}`);
        return;
      }
      
      const response = await hostelService.updateFee(selectedFee.id, {
        ...paymentFormData,
        amount_paid: newTotalAmountPaid
      });
      
      const outstanding = response.outstanding_balance || 0;
      const statusMsg = outstanding <= 0 
        ? 'Fee fully paid!' 
        : `Outstanding balance: GHS ${outstanding.toFixed(2)}`;
      
      toast.success(`Payment recorded successfully. ${statusMsg}. Journal Entry: ${response.journal_entry_id || 'N/A (GL not configured)'}`);
      setShowPaymentForm(false);
      setSelectedFee(null);
      setPaymentFormData({
        amount_paid: '',
        payment_method: 'cash',
        receipt_number: '',
        payment_date: new Date().toISOString().split('T')[0]
      });
      loadData();
    } catch (error) {
      toast.error('Failed to record payment: ' + error.message);
    }
  };
  
  const handleDeleteFee = (id) => {
    setPendingDeleteId(id);
    setPendingDeleteType('fee');
    showDialog({
      title: 'Delete Fee Record',
      message: 'Are you sure you want to delete this fee record?',
      type: 'warning',
      primaryAction: {
        label: 'Delete',
        onClick: async () => {
          closeDialog();
          try {
            await hostelService.deleteFee(id);
            toast.success('Fee deleted');
            loadData();
          } catch (error) {
            toast.error('Failed to delete fee');
          }
        }
      },
      secondaryAction: {
        label: 'Cancel',
        onClick: closeDialog
      }
    });
  };
  
  // ========== Bulk Billing ==========
  const handleBulkBilling = async () => {
    if (!bulkBillingData.hostel_id || !bulkBillingData.bulk_amount) {
      toast.error('Select hostel and enter amount');
      return;
    }
    
    try {
      const selectedHostelStudents = students.filter(s => {
        if (bulkBillingData.class_id) {
          return s.class_id === bulkBillingData.class_id;
        }
        return true;
      });
      
      let successCount = 0;
      for (const student of selectedHostelStudents) {
        try {
          await hostelService.createFee({
            student_id: student.student_id,
            hostel_id: bulkBillingData.hostel_id,
            academic_term_id: bulkBillingData.academic_term_id,
            fee_type: 'term',
            amount_due: parseFloat(bulkBillingData.bulk_amount),
            discount: 0,
            due_date: bulkBillingData.due_date,
            notes: 'Bulk billing'
          });
          successCount++;
        } catch (e) {
          console.error(`Failed to bill student ${student.id}`);
        }
      }
      
      toast.success(`Bulk billing: ${successCount}/${selectedHostelStudents.length} fees created`);
      setShowBulkBillingForm(false);
      setBulkBillingData({
        hostel_id: '',
        academic_term_id: '',
        fee_structure_id: '',
        class_id: '',
        bulk_amount: '',
        due_date: ''
      });
      loadData();
    } catch (error) {
      toast.error('Bulk billing failed');
    }
  };
  
  // ========== Dashboard Statistics ==========
  const calculateStats = () => {
    const totalDue = fees.reduce((sum, f) => sum + (f.amount_due || 0), 0);
    const totalPaid = fees.reduce((sum, f) => sum + (f.amount_paid || 0), 0);
    const totalDiscount = fees.reduce((sum, f) => sum + (f.discount || 0), 0);
    const totalOutstanding = totalDue - totalPaid - totalDiscount;
    const paidCount = fees.filter(f => f.is_paid).length;
    const collectionRate = totalDue > 0 ? ((totalPaid / totalDue) * 100).toFixed(1) : 0;
    
    return { totalDue, totalPaid, totalOutstanding, paidCount, collectionRate, totalDiscount };
  };
  
  if (loading) {
    return <Layout><div className="loading">Loading hostel fees...</div></Layout>;
  }
  
  const stats = calculateStats();
  
  return (
    <Layout>
      <div className="page hostel-page">
        <div className="page-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button className="btn-icon" onClick={() => navigate('/hostel')} title="Back to Hostel">
              <ArrowLeft size={20} weight="bold" />
            </button>
            <div>
              <h1>Hostel Fees & Billing</h1>
              <p>Manage hostel fee structures, billing, and payments with GL integration</p>
            </div>
          </div>
        </div>
        
        {/* Dashboard Stats */}
        <div className="stats-bar">
          <div className="stat">
            <h3>Total Due</h3>
            <p className="stat-value">GHS {stats.totalDue.toFixed(2)}</p>
          </div>
          <div className="stat">
            <h3>Collected</h3>
            <p className="stat-value">GHS {stats.totalPaid.toFixed(2)}</p>
          </div>
          <div className="stat">
            <h3>Outstanding</h3>
            <p className="stat-value">GHS {stats.totalOutstanding.toFixed(2)}</p>
          </div>
          <div className="stat">
            <h3>Collection Rate</h3>
            <p className="stat-value">{stats.collectionRate}%</p>
          </div>
          <div className="stat">
            <h3>Paid/Total</h3>
            <p className="stat-value">{stats.paidCount}/{fees.length}</p>
          </div>
        </div>
        
        {/* Navigation Tabs */}
        <div className="tabs-container">
          <button
            className={`tab ${activeTab === 'fees' ? 'active' : ''}`}
            onClick={() => setActiveTab('fees')}
          >
            Fee Management
          </button>
          <button
            className={`tab ${activeTab === 'structures' ? 'active' : ''}`}
            onClick={() => setActiveTab('structures')}
          >
            Fee Structures
          </button>
          <button
            className={`tab ${activeTab === 'bulk' ? 'active' : ''}`}
            onClick={() => setActiveTab('bulk')}
          >
            Bulk Billing
          </button>
        </div>
        
        {/* ========== FEES TAB ========== */}
        {activeTab === 'fees' && (
          <div>
            <div className="action-bar">
              <button
                className="btn btn-primary"
                onClick={() => { setSelectedFee(null); setShowFeeForm(true); }}
              >
                <Plus size={20} /> Add Fee Record
              </button>
              <div className="search-input">
                <MagnifyingGlass size={20} />
                <input
                  type="text"
                  placeholder="Search student or hostel..."
                  value={feeSearchTerm}
                  onChange={(e) => setFeeSearchTerm(e.target.value)}
                />
              </div>
              <select
                value={feeStatusFilter}
                onChange={(e) => setFeeStatusFilter(e.target.value)}
                className="filter-select"
              >
                <option value="all">All Status</option>
                <option value="paid">Paid</option>
                <option value="unpaid">Unpaid</option>
              </select>
            </div>
            
            {/* Fee List */}
            <div className="table-container">
              {filteredFees.length === 0 ? (
                <div className="empty-state">No fees found</div>
              ) : (
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Student</th>
                      <th>Hostel</th>
                      <th>Type</th>
                      <th>Amount Due</th>
                      <th>Paid</th>
                      <th>Outstanding</th>
                      <th>Due Date</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredFees.map((fee) => (
                      <tr key={fee.id}>
                        <td>{getStudentName(fee.student_id)}</td>
                        <td>{getHostelName(fee.hostel_id)}</td>
                        <td className="capitalize">{fee.fee_type}</td>
                        <td>GHS {fee.amount_due?.toFixed(2) || '0.00'}</td>
                        <td>GHS {fee.amount_paid?.toFixed(2) || '0.00'}</td>
                        <td>GHS {(fee.amount_due - fee.amount_paid - fee.discount)?.toFixed(2) || '0.00'}</td>
                        <td>{fee.due_date || '-'}</td>
                        <td>
                          <span className={`badge badge-${fee.is_paid ? 'success' : 'warning'}`}>
                            {fee.is_paid ? 'PAID' : 'PENDING'}
                          </span>
                        </td>
                        <td className="action-buttons">
                          <button
                            className="btn-icon btn-info"
                            title="View Details"
                            onClick={() => handleViewFeeDetails(fee)}
                          >
                            <Eye size={20} />
                          </button>
                          <button
                            className="btn-icon btn-warning"
                            title="Edit"
                            onClick={() => handleEditFee(fee)}
                          >
                            <PencilSimple size={20} />
                          </button>
                          {!fee.is_paid && (
                            <button
                              className="btn-icon btn-success"
                              title="Record Payment"
                              onClick={() => {
                                setSelectedFee(fee);
                                setShowPaymentForm(true);
                              }}
                            >
                              <CheckCircle size={20} />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            
            {/* Create/Edit Fee Form */}
            {showFeeForm && (
              <div className="modal-overlay">
                <div className="modal">
                  <h2>{selectedFee ? 'Edit Hostel Fee' : 'Create Hostel Fee'}</h2>
                  <form onSubmit={(e) => { e.preventDefault(); handleCreateFee(); }}>
                    <div className="form-group">
                      <FormSelect
                        label="Student *"
                        name="student_id"
                        value={feeFormData.student_id}
                        onChange={(e) => setFeeFormData({...feeFormData, student_id: e.target.value})}
                        required
                        disabled={!!selectedFee}
                        options={students.map(s => ({
                          value: s.student_id,
                          label: `${s.student_id} - ${s.first_name} ${s.last_name}`
                        }))}
                        placeholder="Select Student"
                      />
                    </div>
                    
                    <div className="form-group">
                      <FormSelect
                        label="Hostel *"
                        name="hostel_id"
                        value={feeFormData.hostel_id}
                        onChange={(e) => setFeeFormData({...feeFormData, hostel_id: e.target.value})}
                        required
                        disabled={!!selectedFee}
                        options={hostels.map(h => ({
                          value: h.id,
                          label: h.hostel_name
                        }))}
                        placeholder="Select Hostel"
                      />
                    </div>
                    
                    <div className="form-row">
                      <div className="form-group">
                        <label>Fee Type</label>
                        <select
                          value={feeFormData.fee_type}
                          onChange={(e) => setFeeFormData({...feeFormData, fee_type: e.target.value})}
                        >
                          <option value="monthly">Monthly</option>
                          <option value="term">Term</option>
                          <option value="annual">Annual</option>
                          <option value="semester">Semester</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Amount Due *</label>
                        <input
                          type="number"
                          step="0.01"
                          required
                          value={feeFormData.amount_due}
                          onChange={(e) => setFeeFormData({...feeFormData, amount_due: e.target.value})}
                        />
                      </div>
                    </div>
                    
                    <div className="form-row">
                      <div className="form-group">
                        <label>Discount</label>
                        <input
                          type="number"
                          step="0.01"
                          value={feeFormData.discount}
                          onChange={(e) => setFeeFormData({...feeFormData, discount: e.target.value})}
                        />
                      </div>
                      <div className="form-group">
                        <label>Due Date</label>
                        <input
                          type="date"
                          value={feeFormData.due_date}
                          onChange={(e) => setFeeFormData({...feeFormData, due_date: e.target.value})}
                        />
                      </div>
                    </div>
                    
                    <div className="form-group">
                      <label>Notes</label>
                      <textarea
                        value={feeFormData.notes}
                        onChange={(e) => setFeeFormData({...feeFormData, notes: e.target.value})}
                      />
                    </div>
                    
                    <div className="form-actions">
                      <button type="submit" className="btn btn-primary">{selectedFee ? 'Update Fee' : 'Create Fee'}</button>
                      <button type="button" className="btn btn-secondary" onClick={() => { setShowFeeForm(false); setSelectedFee(null); }}>Cancel</button>
                    </div>
                  </form>
                </div>
              </div>
            )}
            
            {/* Payment Form */}
            {showPaymentForm && selectedFee && (
              <div className="modal-overlay">
                <div className="modal">
                  <h2>Record Payment - {getStudentName(selectedFee.student_id)}</h2>
                  <div className="fee-summary">
                    <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px'}}>
                      <div>
                        <p><strong>Original Amount Due:</strong> GHS {selectedFee.amount_due.toFixed(2)}</p>
                        <p><strong>Already Paid:</strong> GHS {selectedFee.amount_paid.toFixed(2)}</p>
                        <p style={{color: '#d32f2f', fontWeight: 'bold'}}><strong>Outstanding Balance:</strong> GHS {Math.max(0, selectedFee.amount_due - selectedFee.amount_paid - (selectedFee.discount || 0)).toFixed(2)}</p>
                      </div>
                      <div>
                        <p><strong>Discount:</strong> GHS {(selectedFee.discount || 0).toFixed(2)}</p>
                        {paymentFormData.amount_paid && (
                          <>
                            <p><strong>Paying Now:</strong> GHS {parseFloat(paymentFormData.amount_paid).toFixed(2)}</p>
                            <p style={{color: parseFloat(paymentFormData.amount_paid) + selectedFee.amount_paid >= selectedFee.amount_due - (selectedFee.discount || 0) ? '#2e7d32' : '#1976d2', fontWeight: 'bold'}}>
                              <strong>New Balance:</strong> GHS {Math.max(0, selectedFee.amount_due - (parseFloat(paymentFormData.amount_paid) + selectedFee.amount_paid) - (selectedFee.discount || 0)).toFixed(2)}
                            </p>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <form onSubmit={(e) => { e.preventDefault(); handleRecordPayment(); }}>
                    <div className="form-group">
                      <label>Payment Amount * (Max: GHS {Math.max(0, selectedFee.amount_due - selectedFee.amount_paid - (selectedFee.discount || 0)).toFixed(2)})</label>
                      <input
                        type="number"
                        step="0.01"
                        required
                        max={Math.max(0, selectedFee.amount_due - selectedFee.amount_paid - (selectedFee.discount || 0))}
                        min="0.01"
                        value={paymentFormData.amount_paid}
                        onChange={(e) => setPaymentFormData({...paymentFormData, amount_paid: e.target.value})}
                        placeholder="Enter payment amount"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label>Payment Method</label>
                      <select
                        value={paymentFormData.payment_method}
                        onChange={(e) => setPaymentFormData({...paymentFormData, payment_method: e.target.value})}
                      >
                        <option value="cash">Cash</option>
                        <option value="bank_transfer">Bank Transfer</option>
                        <option value="mobile_money">Mobile Money</option>
                        <option value="cheque">Cheque</option>
                      </select>
                    </div>
                    
                    <div className="form-row">
                      <div className="form-group">
                        <label>Receipt Number</label>
                        <input
                          type="text"
                          value={paymentFormData.receipt_number}
                          onChange={(e) => setPaymentFormData({...paymentFormData, receipt_number: e.target.value})}
                        />
                      </div>
                      <div className="form-group">
                        <label>Payment Date</label>
                        <input
                          type="date"
                          value={paymentFormData.payment_date}
                          onChange={(e) => setPaymentFormData({...paymentFormData, payment_date: e.target.value})}
                        />
                      </div>
                    </div>
                    
                    <p className="info-message"><ChartBar size={16} style={{ verticalAlign: 'middle', marginRight: '4px' }} /> This payment will be automatically posted to GL:4150 (Hostel Revenue)</p>
                    
                    <div className="form-actions">
                      <button type="submit" className="btn btn-primary">Record Payment</button>
                      <button type="button" className="btn btn-secondary" onClick={() => { setShowPaymentForm(false); setSelectedFee(null); }}>Cancel</button>
                    </div>
                  </form>
                </div>
              </div>
            )}
            
            {/* Fee Details Modal */}
            {showFeeDetail && selectedFee && (
              <div className="modal-overlay">
                <div className="modal modal-lg">
                  <div className="modal-header">
                    <h2>Fee Details - {getStudentName(selectedFee.student_id)}</h2>
                    <button onClick={() => { setShowFeeDetail(false); setSelectedFee(null); }} className="btn-close">
                      <X size={20} />
                    </button>
                  </div>

                  <div className="modal-body">
                    <div className="detail-section">
                      <h3>Student Information</h3>
                      <div className="detail-row">
                        <div className="detail-item">
                          <label>Student Name</label>
                          <p>{getStudentName(selectedFee.student_id)}</p>
                        </div>
                        <div className="detail-item">
                          <label>Student ID</label>
                          <p>{selectedFee.student_id}</p>
                        </div>
                      </div>
                    </div>

                    <div className="detail-section">
                      <h3>Hostel Information</h3>
                      <div className="detail-row">
                        <div className="detail-item">
                          <label>Hostel Name</label>
                          <p>{getHostelName(selectedFee.hostel_id)}</p>
                        </div>
                        <div className="detail-item">
                          <label>Hostel ID</label>
                          <p>{selectedFee.hostel_id}</p>
                        </div>
                      </div>
                    </div>

                    <div className="detail-section">
                      <h3>Fee Details</h3>
                      <div className="detail-row">
                        <div className="detail-item">
                          <label>Fee Type</label>
                          <p className="capitalize">{selectedFee.fee_type}</p>
                        </div>
                        <div className="detail-item">
                          <label>Amount Due</label>
                          <p>GHS {selectedFee.amount_due.toFixed(2)}</p>
                        </div>
                      </div>
                      <div className="detail-row">
                        <div className="detail-item">
                          <label>Amount Paid</label>
                          <p>GHS {selectedFee.amount_paid?.toFixed(2) || '0.00'}</p>
                        </div>
                        <div className="detail-item">
                          <label>Discount</label>
                          <p>GHS {(selectedFee.discount || 0).toFixed(2)}</p>
                        </div>
                      </div>
                      <div className="detail-row">
                        <div className="detail-item">
                          <label>Outstanding Balance</label>
                          <p>GHS {Math.max(0, selectedFee.amount_due - (selectedFee.amount_paid || 0) - (selectedFee.discount || 0)).toFixed(2)}</p>
                        </div>
                        <div className="detail-item">
                          <label>Payment Status</label>
                          <p>
                            <span className={`badge ${selectedFee.is_paid ? 'badge-success' : 'badge-warning'}`}>
                              {selectedFee.is_paid ? 'PAID' : 'PENDING'}
                            </span>
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="detail-section">
                      <h3>Payment Information</h3>
                      <div className="detail-row">
                        <div className="detail-item">
                          <label>Due Date</label>
                          <p>{selectedFee.due_date || '-'}</p>
                        </div>
                        <div className="detail-item">
                          <label>Payment Date</label>
                          <p>{selectedFee.payment_date || '-'}</p>
                        </div>
                      </div>
                      <div className="detail-row">
                        <div className="detail-item">
                          <label>Payment Method</label>
                          <p>{selectedFee.payment_method || '-'}</p>
                        </div>
                        <div className="detail-item">
                          <label>Receipt Number</label>
                          <p>{selectedFee.receipt_number || '-'}</p>
                        </div>
                      </div>
                    </div>

                    {selectedFee.gl_journal_entry_id && (
                      <div className="detail-section">
                        <h3>GL Posting Information</h3>
                        <div className="detail-row">
                          <div className="detail-item">
                            <label>Journal Entry ID</label>
                            <p>{selectedFee.gl_journal_entry_id}</p>
                          </div>
                          <div className="detail-item">
                            <label>Posted Date</label>
                            <p>{selectedFee.gl_posted_date || '-'}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {selectedFee.notes && (
                      <div className="detail-section">
                        <h3>Notes</h3>
                        <p>{selectedFee.notes}</p>
                      </div>
                    )}

                    <div className="detail-section">
                      <h3>Metadata</h3>
                      <div className="detail-row">
                        <div className="detail-item">
                          <label>Created</label>
                          <p>{new Date(selectedFee.created_at).toLocaleString()}</p>
                        </div>
                        <div className="detail-item">
                          <label>Updated</label>
                          <p>{new Date(selectedFee.updated_at).toLocaleString()}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="modal-footer">
                    <button type="button" className="btn btn-secondary" onClick={() => { setShowFeeDetail(false); setSelectedFee(null); }}>
                      Close
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        
        {/* ========== STRUCTURES TAB ========== */}
        {activeTab === 'structures' && (
          <div>
            <div className="action-bar">
              <button
                className="btn btn-primary"
                onClick={() => { setSelectedStructure(null); setShowFeeStructureForm(true); }}
              >
                <Plus size={20} /> New Structure
              </button>
              <div className="search-input">
                <MagnifyingGlass size={20} />
                <input
                  type="text"
                  placeholder="Search hostel..."
                  value={structureSearchTerm}
                  onChange={(e) => setStructureSearchTerm(e.target.value)}
                />
              </div>
              <select
                value={selectedHostel}
                onChange={(e) => setSelectedHostel(e.target.value)}
                className="filter-select"
              >
                <option value="all">All Hostels</option>
                {hostels.map(h => (
                  <option key={h.id} value={h.id}>{h.hostel_name}</option>
                ))}
              </select>
            </div>
            
            {/* Structure List */}
            <div className="table-container">
              {filteredStructures.length === 0 ? (
                <div className="empty-state">No fee structures defined</div>
              ) : (
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Hostel</th>
                      <th>Fee Type</th>
                      <th>Amount</th>
                      <th>Due Date</th>
                      <th>GL Account</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredStructures.map((structure) => (
                      <tr key={structure.id}>
                        <td>{getHostelName(structure.hostel_id)}</td>
                        <td className="capitalize">{structure.fee_type}</td>
                        <td>GHS {structure.amount.toFixed(2)}</td>
                        <td>{structure.due_date || '-'}</td>
                        <td>{structure.gl_revenue_account_code || 'Not set'}</td>
                        <td>
                          <span className={`badge ${structure.is_active ? 'badge-success' : 'badge-secondary'}`}>
                            {structure.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="action-buttons">
                          <button
                            className="btn-icon btn-info"
                            title="View Details"
                            onClick={() => handleViewStructureDetails(structure)}
                          >
                            <Eye size={20} />
                          </button>
                          <button
                            className="btn-icon btn-warning"
                            title="Edit"
                            onClick={() => handleEditStructure(structure)}
                          >
                            <PencilSimple size={20} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            
            {/* Create/Edit Fee Structure Form */}
            {showFeeStructureForm && (
              <div className="modal-overlay">
                <div className="modal">
                  <h2>{selectedStructure ? 'Edit Fee Structure' : 'Create Fee Structure'}</h2>
                  <form onSubmit={(e) => { e.preventDefault(); handleCreateStructure(); }}>
                    <div className="form-group">
                      <FormSelect
                        label="Hostel *"
                        name="hostel_id"
                        value={structureFormData.hostel_id}
                        onChange={(e) => setStructureFormData({...structureFormData, hostel_id: e.target.value})}
                        required
                        disabled={!!selectedStructure}
                        options={hostels.map(h => ({
                          value: h.id,
                          label: h.hostel_name
                        }))}
                        placeholder="Select Hostel"
                      />
                    </div>
                    
                    <div className="form-row">
                      <div className="form-group">
                        <label>Fee Type</label>
                        <select
                          value={structureFormData.fee_type}
                          onChange={(e) => setStructureFormData({...structureFormData, fee_type: e.target.value})}
                        >
                          <option value="monthly">Monthly</option>
                          <option value="term">Term</option>
                          <option value="annual">Annual</option>
                          <option value="semester">Semester</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Amount *</label>
                        <input
                          type="number"
                          step="0.01"
                          required
                          value={structureFormData.amount}
                          onChange={(e) => setStructureFormData({...structureFormData, amount: e.target.value})}
                        />
                      </div>
                    </div>
                    
                    <div className="form-group">
                      <label>GL Revenue Account Code</label>
                      <input
                        type="text"
                        placeholder="e.g., 4150"
                        value={structureFormData.gl_revenue_account_code}
                        onChange={(e) => setStructureFormData({...structureFormData, gl_revenue_account_code: e.target.value})}
                      />
                    </div>
                    
                    <div className="form-row">
                      <div className="form-group">
                        <label>Due Date</label>
                        <input
                          type="date"
                          value={structureFormData.due_date}
                          onChange={(e) => setStructureFormData({...structureFormData, due_date: e.target.value})}
                        />
                      </div>
                      <div className="form-group">
                        <label>
                          <input
                            type="checkbox"
                            checked={structureFormData.is_active}
                            onChange={(e) => setStructureFormData({...structureFormData, is_active: e.target.checked})}
                          />
                          Active
                        </label>
                      </div>
                    </div>
                    
                    <div className="form-group">
                      <label>Description</label>
                      <textarea
                        value={structureFormData.description}
                        onChange={(e) => setStructureFormData({...structureFormData, description: e.target.value})}
                      />
                    </div>
                    
                    <div className="form-actions">
                      <button type="submit" className="btn btn-primary">{selectedStructure ? 'Update Structure' : 'Create Structure'}</button>
                      <button type="button" className="btn btn-secondary" onClick={() => { setShowFeeStructureForm(false); setSelectedStructure(null); }}>Cancel</button>
                    </div>
                  </form>
                </div>
              </div>
            )}
            
            {/* Structure Details Modal */}
            {showStructureDetail && selectedStructure && (
              <div className="modal-overlay">
                <div className="modal modal-lg">
                  <div className="modal-header">
                    <h2>Fee Structure Details - {getHostelName(selectedStructure.hostel_id)}</h2>
                    <button onClick={() => { setShowStructureDetail(false); setSelectedStructure(null); }} className="btn-close">
                      <X size={20} />
                    </button>
                  </div>

                  <div className="modal-body">
                    <div className="detail-section">
                      <h3>Hostel Information</h3>
                      <div className="detail-row">
                        <div className="detail-item">
                          <label>Hostel Name</label>
                          <p>{getHostelName(selectedStructure.hostel_id)}</p>
                        </div>
                        <div className="detail-item">
                          <label>Hostel ID</label>
                          <p>{selectedStructure.hostel_id}</p>
                        </div>
                      </div>
                    </div>

                    <div className="detail-section">
                      <h3>Fee Structure Details</h3>
                      <div className="detail-row">
                        <div className="detail-item">
                          <label>Fee Type</label>
                          <p className="capitalize">{selectedStructure.fee_type}</p>
                        </div>
                        <div className="detail-item">
                          <label>Amount</label>
                          <p>GHS {selectedStructure.amount.toFixed(2)}</p>
                        </div>
                      </div>
                      <div className="detail-row">
                        <div className="detail-item">
                          <label>GL Revenue Account Code</label>
                          <p>{selectedStructure.gl_revenue_account_code || 'Not configured'}</p>
                        </div>
                        <div className="detail-item">
                          <label>Due Date</label>
                          <p>{selectedStructure.due_date || '-'}</p>
                        </div>
                      </div>
                      <div className="detail-row">
                        <div className="detail-item">
                          <label>Status</label>
                          <p>
                            <span className={`badge ${selectedStructure.is_active ? 'badge-success' : 'badge-secondary'}`}>
                              {selectedStructure.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </p>
                        </div>
                        <div className="detail-item">
                          <label>Academic Term</label>
                          <p>{selectedStructure.academic_term_id || '-'}</p>
                        </div>
                      </div>
                    </div>

                    {selectedStructure.description && (
                      <div className="detail-section">
                        <h3>Description</h3>
                        <p>{selectedStructure.description}</p>
                      </div>
                    )}

                    <div className="detail-section">
                      <h3>Metadata</h3>
                      <div className="detail-row">
                        <div className="detail-item">
                          <label>Created</label>
                          <p>{new Date(selectedStructure.created_at).toLocaleString()}</p>
                        </div>
                        <div className="detail-item">
                          <label>Updated</label>
                          <p>{new Date(selectedStructure.updated_at).toLocaleString()}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="modal-footer">
                    <button type="button" className="btn btn-secondary" onClick={() => { setShowStructureDetail(false); setSelectedStructure(null); }}>
                      Close
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        
        {/* ========== BULK BILLING TAB ========== */}
        {activeTab === 'bulk' && (
          <div>
            <div className="bulk-billing-container">
              <h2>Bulk Billing</h2>
              <p>Generate multiple fees at once for hostel students or a specific class</p>
              
              <form onSubmit={(e) => { e.preventDefault(); handleBulkBilling(); }} className="form-card">
                <div className="form-row">
                  <div className="form-group">
                    <FormSelect
                      label="Hostel *"
                      name="hostel_id"
                      value={bulkBillingData.hostel_id}
                      onChange={(e) => setBulkBillingData({...bulkBillingData, hostel_id: e.target.value})}
                      required
                      options={hostels.map(h => ({
                        value: h.id,
                        label: h.hostel_name
                      }))}
                      placeholder="Select Hostel"
                    />
                  </div>
                  <div className="form-group">
                    <FormSelect
                      label="Class (Optional)"
                      name="class_id"
                      value={bulkBillingData.class_id}
                      onChange={(e) => setBulkBillingData({...bulkBillingData, class_id: e.target.value})}
                      options={classes.map(c => ({
                        value: c.id,
                        label: c.class_name
                      }))}
                      placeholder="All Classes"
                    />
                  </div>
                </div>
                
                <div className="form-row">
                  <div className="form-group">
                    <label>Amount per Student *</label>
                    <input
                      type="number"
                      step="0.01"
                      required
                      value={bulkBillingData.bulk_amount}
                      onChange={(e) => setBulkBillingData({...bulkBillingData, bulk_amount: e.target.value})}
                    />
                  </div>
                  <div className="form-group">
                    <label>Due Date</label>
                    <input
                      type="date"
                      value={bulkBillingData.due_date}
                      onChange={(e) => setBulkBillingData({...bulkBillingData, due_date: e.target.value})}
                    />
                  </div>
                </div>
                
                <div className="form-actions">
                  <button type="submit" className="btn btn-primary"><CreditCard size={16} style={{ verticalAlign: 'middle', marginRight: '4px' }} /> Generate Bulk Fees</button>
                </div>
              </form>
            </div>
          </div>
        )}

      {/* Dialog Modal */}
      <DialogModal
        isOpen={dialogConfig.isOpen}
        onClose={closeDialog}
        title={dialogConfig.title}
        message={dialogConfig.message}
        type={dialogConfig.type}
        primaryAction={dialogConfig.primaryAction}
        secondaryAction={dialogConfig.secondaryAction}
      />
      </div>
    </Layout>
  );
}

export default HostelFeesPage;
